import { useState, useEffect, FC } from "react";
import { useAppDispatch } from "../../app/hooks";
import AdminSubmenu from "../../features/adminSubmenu";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import {
  getOrderTypes,
  putOrderType,
} from "../../app/services/orderTypeService";
import { OrderType } from "../../models/responses/orderType";
import { Col, Row, Spinner, Table, FormControl } from "react-bootstrap";
import { ClientType } from "../../models/enums/clientTypeEnum";

interface Props {}

const AdminSettings: FC<Props> = () => {
  const dispatch = useAppDispatch();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const orderTypes: OrderType[] = useSelector(
    (state: RootState) => state.orderTypes.orderTypeList || []
  );
  const loadingOrderTypes = useSelector(
    (state: RootState) => state.orderTypes.isWaitingOrderTypeGetResponse
  );
  const clientType = useSelector(
    (state: RootState) => state.clientType.clientType
  );
  const clientTypeLabel =
    clientType === ClientType.Standard ? "skadeverkstad" : "AD butik";

  const [discounts, setDiscounts] = useState<{ [id: string]: string }>({});
  const [saved, setSaved] = useState<{ [id: string]: boolean }>({});
  const [saving, setSaving] = useState<{ [id: string]: boolean }>({});

  useEffect(() => {
    dispatch(getOrderTypes(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    setDiscounts(
      Object.fromEntries(
        orderTypes.map((o) => [o.id, o.discount?.toString() ?? ""])
      )
    );
  }, [orderTypes]);

  const handleDiscountChange = (id: string, value: string) => {
    setDiscounts((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = (orderType: OrderType) => {
    const newDiscount = discounts[orderType.id];
    setSaving((prev) => ({ ...prev, [orderType.id]: true }));
    dispatch(
      putOrderType(instance, account, {
        ...orderType,
        discount: Number(newDiscount),
      })
    ).finally(() => {
      setSaving((prev) => ({ ...prev, [orderType.id]: false }));
      setSaved((prev) => ({ ...prev, [orderType.id]: true }));
      setTimeout(() => {
        setSaved((prev) => ({ ...prev, [orderType.id]: false }));
      }, 2000);
    });
  };

  return (
    <div>
      <AdminSubmenu />
      <h2 className="mb-3">Ordertyper för {clientTypeLabel}</h2>
      <p>Här kan du hantera olika ordertyper för {clientTypeLabel}.</p>
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>Namn</th>
            {clientType === ClientType.Standard && <th>Rabatt (%)</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {orderTypes.map((orderType) => (
            <tr key={orderType.id}>
              <td>{orderType.name}</td>
              {clientType === ClientType.Standard && (
                <td>
                  <FormControl
                    type="number"
                    value={discounts[orderType.id] ?? ""}
                    onChange={(e) =>
                      handleDiscountChange(orderType.id, e.currentTarget.value)
                    }
                    style={{ width: "80px" }}
                    disabled={saving[orderType.id]}
                  />
                </td>
              )}
              <td>
                {saving[orderType.id] ? (
                  <Spinner size="sm" animation="border" role="status" />
                ) : saved[orderType.id] ? (
                  <span style={{ color: "green" }}>Sparad</span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleSave(orderType)}
                  >
                    Spara
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {loadingOrderTypes && (
        <>
          <Row className="mb-3">
            <Col className="text-center">
              <Spinner variant="primary" />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default AdminSettings;
